import { ButtonsWrapper, StyledButton } from './ApplicationFooter.styles';
import { Icon, useTheme, useAppDispatch, useAppSelector } from '../../../base';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Directions, getBasicColorsObject } from '../../../common';
import {
    jobApplicationActions,
    jobApplicationSelectors,
} from '../../../actions';

const isEditableKeyboardTarget = (target: EventTarget | null) => {
    if (!(target instanceof HTMLElement)) {
        return false;
    }

    const tagName = target.tagName;
    return (
        tagName === 'INPUT' ||
        tagName === 'TEXTAREA' ||
        tagName === 'SELECT' ||
        target.isContentEditable
    );
};

const ApplicationFooter = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const { direction: themeDirection } = theme;

    const nextCandidateId = useAppSelector(
        jobApplicationSelectors.getNextJobApplicationsId
    );
    const previousCandidateId = useAppSelector(
        jobApplicationSelectors.getPreviousJobApplicationsId
    );
    const onPreviousClick = () => {
        previousCandidateId &&
            dispatch(
                jobApplicationActions.loadNextOrPrevCandidate({
                    candidateId: previousCandidateId,
                    baseColors: getBasicColorsObject(theme.palette),
                })
            );
    };
    const onNextClick = () => {
        nextCandidateId &&
            dispatch(
                jobApplicationActions.loadNextOrPrevCandidate({
                    candidateId: nextCandidateId,
                    baseColors: getBasicColorsObject(theme.palette),
                })
            );
    };

    useEffect(() => {
        // Define the keydown event handler
        const handleKeyDown = (event: KeyboardEvent) => {
            if (isEditableKeyboardTarget(event.target)) {
                return;
            }

            switch (event.key) {
                case 'ArrowLeft':
                    if (themeDirection === Directions.rtl) {
                        onNextClick();
                    } else {
                        onPreviousClick();
                    }
                    break;
                case 'ArrowRight':
                    if (themeDirection === Directions.rtl) {
                        onPreviousClick();
                    } else {
                        onNextClick();
                    }
                    break;
                default:
                    break;
            }
        };

        // Add the event listener
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [themeDirection]);

    const NavigationButton = (
        label: string,
        onClick: () => void,
        direction: string,
        candidateId: number | null
    ) => {
        const IconComponent =
            direction === 'previous' ? (
                <Icon
                    name={
                        themeDirection === Directions.rtl
                            ? 'ForwardIcon'
                            : 'BackIcon'
                    }
                />
            ) : (
                <Icon
                    name={
                        themeDirection === Directions.rtl
                            ? 'BackIcon'
                            : 'ForwardIcon'
                    }
                />
            );

        return (
            <StyledButton
                onClick={onClick}
                startIcon={direction === 'previous' ? IconComponent : undefined}
                endIcon={direction === 'next' ? IconComponent : undefined}
                disabled={!candidateId}
            >
                {label}
            </StyledButton>
        );
    };

    return (
        <ButtonsWrapper>
            {NavigationButton(
                t('jobApplicationPage.previous'),
                onPreviousClick,
                'previous',
                previousCandidateId
            )}
            {NavigationButton(
                t('jobApplicationPage.next'),
                onNextClick,
                'next',
                nextCandidateId
            )}
        </ButtonsWrapper>
    );
};

export default ApplicationFooter;
